import { ExapReviewState, HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '../../models';
import { LocDisplayType } from '../../models/display-type';
import { SYSTEM_ENUM_ROOT, SYSTEM_ENUM_TYPE } from '../shared-data';

const dpRoot = `${SYSTEM_ENUM_ROOT}|Exam Accommodations Review State|`;

type SharedKeys = Pick<
	LocDef,
	'type' | 'portal' | 'displayType' | 'htmlAllowed' | 'shared' | 'feature'
>;

const sharedLocData: Pick<LocDef, 'displayType' | 'htmlAllowed' | 'shared' | 'feature'> = {
	displayType: `${SYSTEM_ENUM_TYPE}` as LocDisplayType,
	htmlAllowed: false,
	shared: true,
	feature: HubFeature.ExamAccommodations,
};

const sharedSpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'SP',
};

const sharedCpLocData: SharedKeys = {
	...sharedLocData,
	portal: 'CP',
};

export type SPExapReviewStateLocs = `SPExapReviewStateEnum${ExapReviewState}`;

export const spExapReviewStateEnumLocs: LocDefs<SPExapReviewStateLocs> = {
	SPExapReviewStateEnumApproved: {
		defaultValue: 'Approved',
		displayPath: `${dpRoot}${ExapReviewState.Approved}`,
		description: `System value for '${ExapReviewState.Approved}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Approved',
		...sharedSpLocData,
	},
	SPExapReviewStateEnumDenied: {
		defaultValue: 'Denied',
		displayPath: `${dpRoot}${ExapReviewState.Denied}`,
		description: `System value for '${ExapReviewState.Denied}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Denied',
		...sharedSpLocData,
	},
	SPExapReviewStateEnumNone: {
		defaultValue: 'None',
		displayPath: `${dpRoot}${ExapReviewState.None}`,
		description: `System value for '${ExapReviewState.None}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.None',
		...sharedSpLocData,
	},
	SPExapReviewStateEnumPending: {
		defaultValue: 'Pending',
		displayPath: `${dpRoot}${ExapReviewState.Pending}`,
		description: `System value for '${ExapReviewState.Pending}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Pending',
		...sharedSpLocData,
	},
	SPExapReviewStateEnumRevoked: {
		defaultValue: 'Revoked',
		displayPath: `${dpRoot}${ExapReviewState.Revoked}`,
		description: `System value for '${ExapReviewState.Revoked}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Revoked',
		...sharedSpLocData,
	},
};

export type CPExapReviewStateLocs = `CPExapReviewStateEnum${ExapReviewState}`;

export const cpExapReviewStateEnumLocs: LocDefs<CPExapReviewStateLocs> = {
	CPExapReviewStateEnumApproved: {
		defaultValue: 'Approved',
		displayPath: `${dpRoot}${ExapReviewState.Approved}`,
		description: `System value for '${ExapReviewState.Approved}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Approved',
		...sharedCpLocData,
	},
	CPExapReviewStateEnumDenied: {
		defaultValue: 'Denied',
		displayPath: `${dpRoot}${ExapReviewState.Denied}`,
		description: `System value for '${ExapReviewState.Denied}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Denied',
		...sharedCpLocData,
	},
	CPExapReviewStateEnumNone: {
		defaultValue: 'None',
		displayPath: `${dpRoot}${ExapReviewState.None}`,
		description: `System value for '${ExapReviewState.None}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.None',
		...sharedCpLocData,
	},
	CPExapReviewStateEnumPending: {
		defaultValue: 'Pending',
		displayPath: `${dpRoot}${ExapReviewState.Pending}`,
		description: `System value for '${ExapReviewState.Pending}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Pending',
		...sharedCpLocData,
	},
	CPExapReviewStateEnumRevoked: {
		defaultValue: 'Revoked',
		displayPath: `${dpRoot}${ExapReviewState.Revoked}`,
		description: `System value for '${ExapReviewState.Revoked}' review state.`,
		legacyName: 'Exam.Accommodations/System.Enum.ExapReviewState.Revoked',
		...sharedCpLocData,
	},
};

export const EX_AP_REVIEW_STATE_ENUM_LOCS = MapLocKeys<
	SPExapReviewStateLocs | CPExapReviewStateLocs
>({
	...spExapReviewStateEnumLocs,
	...cpExapReviewStateEnumLocs,
});

export type SPExapReviewStateEnumLocType = LocMap<SPExapReviewStateLocs>;
export type CPExapReviewStateEnumLocType = LocMap<CPExapReviewStateLocs>;

export const spExapReviewStateKeys: {
	[key in ExapReviewState]: keyof SPExapReviewStateEnumLocType;
} = {
	[ExapReviewState.Approved]: EX_AP_REVIEW_STATE_ENUM_LOCS.SPExapReviewStateEnumApproved,
	[ExapReviewState.Denied]: EX_AP_REVIEW_STATE_ENUM_LOCS.SPExapReviewStateEnumDenied,
	[ExapReviewState.None]: EX_AP_REVIEW_STATE_ENUM_LOCS.SPExapReviewStateEnumNone,
	[ExapReviewState.Pending]: EX_AP_REVIEW_STATE_ENUM_LOCS.SPExapReviewStateEnumPending,
	[ExapReviewState.Revoked]: EX_AP_REVIEW_STATE_ENUM_LOCS.SPExapReviewStateEnumRevoked,
};

export const cpExapReviewStateKeys: {
	[key in ExapReviewState]: keyof CPExapReviewStateEnumLocType;
} = {
	[ExapReviewState.Approved]: EX_AP_REVIEW_STATE_ENUM_LOCS.CPExapReviewStateEnumApproved,
	[ExapReviewState.Denied]: EX_AP_REVIEW_STATE_ENUM_LOCS.CPExapReviewStateEnumDenied,
	[ExapReviewState.None]: EX_AP_REVIEW_STATE_ENUM_LOCS.CPExapReviewStateEnumNone,
	[ExapReviewState.Pending]: EX_AP_REVIEW_STATE_ENUM_LOCS.CPExapReviewStateEnumPending,
	[ExapReviewState.Revoked]: EX_AP_REVIEW_STATE_ENUM_LOCS.CPExapReviewStateEnumRevoked,
};
